import FindMeetingTimesRequest from '../schema/FindMeetingTimesRequest';
import { findMeetingTimesConstants } from '../constants';
import { makePostRequest } from 'owa-ows-gateway';
import FindMeetingTimesResponse from '../schema/FindMeetingTimesResponse';
import { logFindMeetingTimesRequestDiagnostics } from 'owa-calendar-cas-diagnostics';
import { isFeatureEnabled } from 'owa-feature-flags';

/**
 * Makes the findMeetingTimes request
 * @param request The request object
 * @param scenarioNameForHeader The name for the scenario which will be sent in the header
 */
export function findMeetingTimesService(
    request: FindMeetingTimesRequest,
    scenarioNameForHeader: string
): Promise<Response> {
    const headers = {};
    headers[findMeetingTimesConstants.APPNAME_HEADERNAME] =
        findMeetingTimesConstants.APPNAME_HEADERVALUE;
    headers[findMeetingTimesConstants.SCENARIONAME_HEADERNAME] = scenarioNameForHeader;

    if (isFeatureEnabled('cal-mf-timeAccommodations')) {
        // We want results with accommodation suggestions from the server instead of the ranked results, so set the header
        headers[findMeetingTimesConstants.RETURNACCOMMODATIONS_HEADERNAME] =
            findMeetingTimesConstants.RETURNACCOMMODATIONS_HEADERVALUE;
    } else {
        // We want chronological results from the server instead of the ranked results, so set the header
        headers[findMeetingTimesConstants.RETURNCHRONOLOGICALRESULTS_HEADERNAME] =
            findMeetingTimesConstants.RETURNCHRONOLOGICALRESULTS_HEADERVALUE;
    }

    // This experiment is for the server to suggest meeting
    // times based on user's preferred meeting hours, inferred
    // from their historical working hour preference.
    const isUsePreferredMeetingTimesExp = isFeatureEnabled(
        'cal-mf-timeSuggestions-exp-usePreferredMeetingTimes'
    );
    if (isUsePreferredMeetingTimesExp) {
        // We want result time slots to be within user's preferred meeting times.
        // By default, the result times lots are within user's configured working hours.
        headers[findMeetingTimesConstants.USEPREFERREDMEETINGTIMES_HEADERNAME] =
            findMeetingTimesConstants.USEPREFERREDMEETINGTIMES_HEADERVALUE;
    }

    // Add debug info
    headers[findMeetingTimesConstants.DEBUGINFO_HEADERNAME] =
        findMeetingTimesConstants.DEBUGINFO_HEADERVALUE;

    return makePostRequest(
        isFeatureEnabled('cal-mf-placesb2')
            ? findMeetingTimesConstants.FMT_ENDPOINT_URLB2
            : findMeetingTimesConstants.FMT_ENDPOINT_URL,
        request,
        undefined /* correlationId */,
        true /* returnFullResponse */,
        headers /* customHeaders */,
        undefined /* throwServiceError */,
        undefined /* sendPayloadAsBody */,
        undefined /* includeCredentials */,
        'findMeetingTimes' /* actionName */
    ).then((response: Response) => {
        // Log response
        if (response && typeof response.clone == 'function') {
            generateDataPoint(response).then(datapoint => {
                if (isFeatureEnabled('cal-mf-diagnostics')) {
                    logFindMeetingTimesRequestDiagnostics({
                        requestId: datapoint.requestId,
                        status: datapoint.status,
                        requestTime: datapoint.requestTime,
                        server: datapoint.server,
                        debug: datapoint.debug,
                    });
                }
            });
        }

        return response;
    });
}

async function generateDataPoint(response: Response) {
    let requestId = '';
    let server = '';
    let debug = '';
    let dateTime;
    let timeSuggestions: FindMeetingTimesResponse = await response
        .clone()
        .json()
        .catch(e => {
            e.networkError = true;
            throw e;
        });

    if (response.headers) {
        if (response.headers.has('request-id')) {
            requestId = response.headers.get('request-id');
        }
        if (response.headers.has('x-calculatedbetarget')) {
            server = response.headers.get('x-calculatedbetarget');
        }
        if (response.headers.has('date')) {
            dateTime = new Date(response.headers.get('date'));
        }
    }

    if (timeSuggestions?.DebugInfo) {
        debug = timeSuggestions.DebugInfo;
    }

    return {
        requestId: requestId,
        status: response ? `${response.status} ${response.statusText}` : 'Error',
        requestTime: dateTime
            ? dateTime.toLocaleDateString() + ' ' + dateTime.toLocaleTimeString()
            : '',
        server: server,
        debug: debug,
    };
}
